export enum TaskState {
  CLASSIFIED = 'CLASSIFIED',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  APPROVED = 'APPROVED',
  EXECUTING = 'EXECUTING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  WAITING_RESPONSE = 'WAITING_RESPONSE',
  NO_RESPONSE = 'NO_RESPONSE',
}

export type SubscriptionTier = 'starter' | 'pro' | 'max';
export type SubscriptionStatus = 'active' | 'cancelled' | 'past_due';
export type Channel = 'voice' | 'email';
export type ContactSource = 'vault' | 'web' | 'user_provided';
export type EvidenceType = 'call' | 'email';
export type EvidenceOutcome = 'success' | 'failed' | 'no_answer' | 'no_response' | 'partial';
export type EmailReplyClassification = 'positive' | 'negative' | 'no_action';
export type NotificationType = 'task_update' | 'usage_warning' | 'system';
export type MessageType = 'user_input' | 'classification' | 'plan' | 'approval' | 'execution' | 'result' | 'info_retrieval' | 'system_message';

export interface ClassificationResult {
  intent: string;
  channel: 'voice' | 'email' | null;
  company: string | null;
  goal: string;
  confidence: number;
  fallbackChannel: string | null;
}

export interface TaskRecord {
  id: string;
  userId: string;
  intent: string;
  channel: 'voice' | 'email';
  company: string | null;
  goal: string;
  confidence: number;
  fallbackChannel: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  contactSource: ContactSource | null;
  talkingPoints: string | null;
  emailSubject: string | null;
  emailBody: string | null;
  state: TaskState;
  approvalTimestamp: string | null;
  executionStart: string | null;
  executionEnd: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EvidenceRecord {
  id: string;
  taskId: string;
  type: EvidenceType;
  attemptNumber: number;
  outcome: EvidenceOutcome;
  timestamp: string;
  callDuration: number | null;
  repName: string | null;
  transcript: string | null;
  recordingUrl: string | null;
  summary: string | null;
  emailRecipient: string | null;
  emailSubject: string | null;
  emailBody: string | null;
  emailSentAt: string | null;
  emailReply: string | null;
  emailReplyAt: string | null;
  emailReplyClassification: EmailReplyClassification | null;
  followUpSentAt: string | null;
  createdAt: string;
}

export interface VaultContactRecord {
  id: string;
  userId: string;
  name: string;
  phone: string;
  email: string | null;
  company: string | null;
  source: ContactSource;
  relationType: string | null;
  lastUsed: string | null;
  inferredContext: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserRecord {
  id: string;
  clerkId: string;
  email: string;
  phoneNumber: string | null;
  phoneVerified: boolean;
  gmailConnected: boolean;
  gmailRefreshToken: string | null;
  subscriptionTier: SubscriptionTier;
  callsUsedThisMonth: number;
  callsLimit: number;
  emailsUsedThisMonth: number;
  emailsLimit: number;
  usagePeriodStart: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubscriptionRecord {
  id: string;
  userId: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationRecord {
  id: string;
  userId: string;
  taskId: string | null;
  type: NotificationType;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
}

export interface QueryLogRecord {
  id: string;
  userId: string;
  taskId: string | null;
  messageType: MessageType;
  action: string;
  inputText: string | null;
  classification: ClassificationResult | null;
  planDetails: Record<string, unknown> | null;
  executionDetails: Record<string, unknown> | null;
  infoResponse: string | null;
  timestamp: string;
  searchableText: string;
  createdAt: string;
}

export interface UserPreferencesRecord {
  id: string;
  userId: string;
  preferencesJson: string;
  lastUpdated: string;
}

export interface UsageCheckResult {
  allowed: boolean;
  used: number;
  limit: number;
  tier: string;
}
